<template>
  <section class="people-card" aria-label="Active people by role">
    <header class="people-card__header">
      <h3 class="people-card__title">Active people by role</h3>
      <p class="people-card__subtitle">
        Signed in and completed at least one action in the last 30 days.
      </p>
    </header>

    <div v-if="roles.length === 0" class="people-card__empty">No role activity yet.</div>

    <div v-for="role in roles" v-else :key="role.role" class="people-card__row">
      <div class="people-card__role-head">
        <span class="people-card__role-name">{{ role.role }}</span>
        <span class="people-card__role-count">{{ activeOfTotal(role) }}</span>
      </div>
      <div
        class="people-card__progress"
        role="progressbar"
        :aria-valuenow="roleProgress(role)"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="`${role.role} ${activeOfTotal(role)}`"
      >
        <div class="people-card__progress-fill" :style="{ width: `${roleProgress(role)}%` }"></div>
      </div>
      <p v-if="role.hint" class="people-card__hint">{{ role.hint }}</p>
    </div>
  </section>
</template>

<script setup>
import { defineProps } from "vue";

defineProps({
  roles: {
    type: Array,
    default: () => [],
  },
});

function activeOfTotal(role) {
  const active = Number(role.active);
  const total = Number(role.total);
  if (!Number.isFinite(active) || !Number.isFinite(total)) return "—";
  return `${active} of ${total}`;
}

function roleProgress(role) {
  const active = Number(role.active);
  const total = Number(role.total);
  if (!Number.isFinite(active) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.min(100, (active / total) * 100));
}
</script>

<style scoped>
.people-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.people-card__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px;
}

.people-card__title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.people-card__subtitle {
  margin: 0;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.people-card__empty {
  padding: 16px 20px;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  border-top: 1px solid #eaecf0;
}

.people-card__row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #eaecf0;
}

.people-card__role-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.people-card__role-name {
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.people-card__role-count {
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

.people-card__progress {
  height: 8px;
  border-radius: 8px;
  background: #eaecf0;
  overflow: hidden;
}

.people-card__progress-fill {
  height: 100%;
  border-radius: 8px;
  background: #7f56d9;
}

.people-card__hint {
  margin: 0;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
}
</style>
